import React from 'react'

import { useTenant } from '@rewardops/auth'
import {
  Button,
  cn,
  LocalizedLink,
  Spinner,
  type ButtonProps,
} from '@rewardops/ui'

import { styling } from '~/components/styling'

interface MapleButtonProps extends ButtonProps {
  children?: React.ReactNode
  id: string
  className?: string
  onClick?: () => void
  onMouseEnter?: () => void
  buttonStyle?: 'primary' | 'secondary'
  buttonType?: 'button' | 'link'
  link?: string
  showLoader?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  type?: 'submit' | 'button' | 'reset'
}

const Loading = () => <Spinner className="text-white" />

export default function MapleButton({
  children,
  id,
  className,
  onClick,
  onMouseEnter,
  link,
  buttonStyle = 'primary',
  buttonType = 'button',
  size = 'default',
  disabled = false,
  showLoader = false,
  icon,
  iconPosition = 'left',
  type = 'button',
}: MapleButtonProps) {
  const tenant = useTenant()

  // Safely check that tenant.styleVariant?.title is a valid key in styling
  const styleVariant = (
    tenant.styleVariant?.title && tenant.styleVariant.title in styling
      ? tenant.styleVariant.title
      : 'default'
  ) as keyof typeof styling

  const btnStyling = [
    (styling[styleVariant] as any).button.base,
    (styling[styleVariant] as any).button[buttonStyle as any],
    (styling[styleVariant] as any).button.size[size as any],
  ]

  const buttonContent = (
    <>
      {showLoader ? (
        <Loading />
      ) : (
        <div className="flex items-center gap-2">
          {iconPosition === 'left' && icon}
          {children}
          {iconPosition === 'right' && icon}
        </div>
      )}
    </>
  )

  if (buttonType === 'link') {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        id={id}
        className={cn(...btnStyling, className)}
        asChild={!disabled}
      >
        <LocalizedLink href={link ?? '/'} disabled={disabled}>
          {buttonContent}
        </LocalizedLink>
      </Button>
    )
  }

  const handleClick = showLoader ? undefined : onClick

  return (
    <Button
      disabled={disabled}
      id={id}
      className={cn(...btnStyling, className)}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      type={type || 'button'}
    >
      {buttonContent}
    </Button>
  )
}
